import type { WhereSelection } from "@ui/components/SearchBar";
import { useSearchUrlSync } from "@/hooks/use-search-url-sync";
import { serializeWhenParams, serializeWhereParams } from "@/lib/search-params";
import type { Bbox } from "@/lib/where-bounding-boxes";
import type { WhereFilter } from "@/lib/where-regions";
import type { DestinationFilters } from "./DestinationsFiltersAdapter";
import { useDestinationsSort } from "./DestinationsSortContext";
import {
  DESTINATIONS_SEARCH_PARAM_KEYS,
  serializeDestinationsFilterParams,
  serializeDestinationsSort,
} from "./search-params";

interface DestinationsUrlSyncProps {
  filters: DestinationFilters;
  whereValue: WhereSelection | null;
  whereBbox: Bbox | null;
  whereFilter: WhereFilter | null;
}

/**
 * Renderless writer mirroring the destinations search state into the URL.
 * Everything serializes from page-local state (the filter panel's five lists
 * live outside InstantSearch); only sort comes from the sort context. Renders
 * under DestinationsSortProvider, beside DestinationsFiltersRefinements.
 */
export function DestinationsUrlSync({ filters, whereValue, whereBbox, whereFilter }: DestinationsUrlSyncProps) {
  const sort = useDestinationsSort();
  useSearchUrlSync({
    params: {
      ...(whereValue && (whereBbox || whereFilter)
        ? serializeWhereParams({ selection: whereValue, bbox: whereBbox, filter: whereFilter })
        : {}),
      ...serializeWhenParams(filters.whenSelection),
      ...serializeDestinationsFilterParams(filters),
      sort: serializeDestinationsSort(sort.currentValue),
    },
    ownedKeys: DESTINATIONS_SEARCH_PARAM_KEYS,
  });
  return null;
}
